/**
 * LiveMeetingPage
 * TASK-393: Meeting Flow Simplification
 *
 * Route wrapper that fetches meeting details and renders the silent recorder.
 *
 * TASK-665: LiveCockpit (21 files, 3,753 lines) was deleted and replaced by
 * MeetingRecorder. `briefData` is no longer fetched here — the cockpit was its only
 * consumer, and the brief belongs on the prep screen, not in front of a live client.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import MeetingRecorder from '../components/meeting/MeetingRecorder';
import SessionExpiryBanner from '../components/meeting/SessionExpiryBanner';
import { meetingApi, type MeetingDetails } from '../services/api/meetingApi';
import { logger } from '../utils/logger';

const LiveMeetingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState<MeetingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('No meeting ID provided');
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadMeetingData = async () => {
      try {
        // TASK-665: the brief fetch went with the cockpit — the recorder shows no
        // content, and the brief lives on /meeting/:id/prepare.
        const details = await meetingApi.getMeetingDetails(id);
        if (!mounted) return;
        setMeeting(details);
      } catch (err) {
        if (mounted) {
          logger.error('[LiveMeetingPage] Unexpected error:', err);
          setError('Failed to load meeting data');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadMeetingData();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <p className="text-sm text-gray-600">Loading meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-blue-600 hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SessionExpiryBanner />
      <MeetingRecorder
        sessionId={id}
        clientId={meeting?.client_id}
        clientName={meeting?.client_name}
        advisorId={meeting?.advisor_id}
        meetingType={meeting?.meeting_type}
        meetingUrl={meeting?.meeting_url}
        onEndMeeting={() => navigate(`/meeting/${id}/review`)}
      />
    </>
  );
};

export default LiveMeetingPage;
